import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SnackbarKey, VariantType } from 'notistack';

export type NotificationType = VariantType;

interface NotificationOptions {
  readonly variant: NotificationType;
}

export interface NotificationLinkButton {
  readonly text: string;
  readonly href: string;
}

export interface Notification {
  readonly key: SnackbarKey;
  readonly addedAt: number;
  readonly options: NotificationOptions;
  readonly message: string;
  readonly dismissed?: boolean;
  readonly permanent?: boolean;
  readonly waitForDismissal?: boolean;
  readonly linkButton?: NotificationLinkButton;
}

interface Dialog {
  readonly message: string;
}

interface NotificationsState {
  readonly snackbars: readonly Notification[];
  readonly dialog: Dialog | null;
}

const initialState: NotificationsState = {
  snackbars: [],
  dialog: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    enqueueSnackbar: (state, action: PayloadAction<Notification>) => {
      // Avoid duplicating permanent snackbars.
      if (
        action.payload.permanent &&
        state.snackbars.some((notification) => notification.message === action.payload.message)
      ) {
        return state;
      }

      state.snackbars = [...state.snackbars, action.payload];
    },
    closeSnackbar: (state, action: PayloadAction<SnackbarKey>) => {
      state.snackbars = state.snackbars.filter((snackbar) => snackbar.key !== action.payload);
    },
    closeSnackbarsWithMessage: (state, action: PayloadAction<string>) => {
      state.snackbars = state.snackbars.filter((snackbar) => snackbar.message !== action.payload);
    },
    closeAllSnackbars: (state) => {
      state.snackbars = state.snackbars.filter((snackbar) => snackbar.permanent);
    },
    addDialog: (state, action: PayloadAction<string>) => {
      state.dialog = { message: action.payload };
    },
    dismissDialog: (state) => {
      state.dialog = null;
    },
  },
});

export const {
  enqueueSnackbar,
  closeSnackbar,
  closeSnackbarsWithMessage,
  closeAllSnackbars,
  addDialog,
  dismissDialog,
} = notificationsSlice.actions;

export const notifications = notificationsSlice.reducer;

export interface StateWithNotifications {
  notifications: NotificationsState;
}
